/* eslint-disable react/react-in-jsx-scope */
import { EntityId } from 'entities-next/common'
import { getApiSearchFunctionHandlers, SEARCH_FUNCTION_CACHE_STORE_ID } from 'entities-next/searchFunctions'
import ArrowRightIcon from 'mdi-react/ArrowRightIcon'
import { QueryFunctionContext, useMutation, useQuery } from 'react-query'
import { usePaginatedSearch } from 'scenes/Dashboard/components-next/PaginatedSearch'
import TernarySelector, {
  SelectableOption,
} from 'scenes/Dashboard/components/AssessmentsView/components/AssessmentTable/components/AssessmentRow/components/TernarySelector'
import { DebouncedInput } from 'scenes/Dashboard/components/DebouncedInput'
import { makeStyles } from 'tss-react/mui'
import { key } from 'utils'
import { format } from 'utils-next/dates'
import { Link, useMatchParams } from 'utils-next/routes'
import { ExtendableStyles, PaginationOptions, Testable } from 'utils-next/types'

import { IconButton, Theme, Typography } from '@mui/material'
import { DataGrid, GridCellParams, GridColDef, GridValueFormatterParams } from '@mui/x-data-grid'

import { getScoutingCampaignSearchCandidatesResultsViewPath } from '../../ScoutingCampaignDetailsView.routes'
import { getApiSearchCandidatesHandlers } from './SearchCandidates.api'
import { SearchCandidate, SearchCandidatesFilters } from './SearchCandidates.types'

export type SearchCandidatesListProps = ExtendableStyles & Testable

export function SearchCandidatesList() {
  const { classes } = useStyles()

  const { scoutingCampaignId } = useMatchParams()
  const { state } = useSearchCandidatesStore({ searchFunctionId: Number(scoutingCampaignId) })
  const searchCandidates = state.data

  const { apiPatchSearchCandidate } = getApiSearchCandidatesHandlers()
  const { mutate } = useMutation(apiPatchSearchCandidate)

  const getLongTextViewer = (paramName: keyof SearchCandidate) => {
    return function longTextView(params: GridCellParams) {
      const longText = params.getValue(params.id, paramName) as string | undefined
      return (
        <Typography className={classes.longTextView} variant="body2">
          {longText}
        </Typography>
      )
    }
  }

  const columns: GridColDef[] = [
    { field: key<SearchCandidate>('id'), headerName: 'ID', width: 90, description: 'Search candidate Id' },
    {
      field: key<SearchCandidate>('createdAt'),
      headerName: 'Created at',
      width: 120,
      type: 'date',
      valueFormatter: (params: GridValueFormatterParams) => {
        const createdAt =
          typeof params.value === 'function' && (params.value(params.id, 'createdAt') as string | undefined)
        return createdAt ? format(new Date(createdAt), 'yyyy-MM-dd') : undefined
      },
    },
    {
      field: key<SearchCandidate>('positivePrimaryKeywords'),
      headerName: '+ Primary keywords',
      width: 300,
      renderCell: getLongTextViewer('positivePrimaryKeywords'),
    },
    {
      field: 'linkToSearchEngine',
      headerName: 'Go!',
      sortable: false,
      width: 80,
      renderCell: function goToSearchEngine(params: GridCellParams) {
        const searchCandidateId = params.getValue(params.id, 'id') as EntityId
        return (
          <Link
            to={getScoutingCampaignSearchCandidatesResultsViewPath(
              Number(scoutingCampaignId),
              Number(searchCandidateId)
            )}
          >
            <IconButton key="go-to-search-engine" aria-label="Go to search engine" color="inherit">
              <ArrowRightIcon color="inherit" />
            </IconButton>
          </Link>
        )
      },
    },
    {
      field: key<SearchCandidate>('positiveSecondaryKeywords'),
      headerName: '+ Secondary keywords',
      width: 300,
      renderCell: getLongTextViewer('positiveSecondaryKeywords'),
      hide: true,
    },
    {
      field: key<SearchCandidate>('negativePrimaryKeywords'),
      headerName: '– Primary keywords',
      width: 300,
      renderCell: getLongTextViewer('negativePrimaryKeywords'),
      hide: true,
    },
    {
      field: key<SearchCandidate>('negativeSecondaryKeywords'),
      headerName: '– Secondary keywords',
      width: 300,
      renderCell: getLongTextViewer('negativeSecondaryKeywords'),
      hide: true,
    },
    {
      field: key<SearchCandidate>('locations'),
      headerName: 'Locations',
      width: 200,
      renderCell: getLongTextViewer('locations'),
      hide: true,
    },
    {
      field: key<SearchCandidate>('stages'),
      headerName: 'Last funding event',
      width: 200,
      renderCell: getLongTextViewer('stages'),
      hide: true,
    },
    {
      field: key<SearchCandidate>('isUseful'),
      headerName: 'Is useful?',
      width: 140,
      renderCell: function isUsefulToggle(params: GridCellParams) {
        return (
          <TernarySelector
            selectedOption={params.row.isUseful}
            onSelect={(selectedOption) => {
              const thisSearchCandidate = searchCandidates?.find((searchCandidate) => searchCandidate.id === params.id)
              if (!thisSearchCandidate) {
                return
              }
              const isUseful = {
                [SelectableOption.Unknown]: undefined,
                [SelectableOption.Yes]: true,
                [SelectableOption.No]: false,
              }[selectedOption]

              mutate({
                searchFunctionId: Number(scoutingCampaignId),
                searchCandidateId: Number(thisSearchCandidate.id),
                opsNotes: thisSearchCandidate.opsNotes,
                isUseful: isUseful,
              })
              thisSearchCandidate.isUseful = selectedOption
            }}
          />
        )
      },
    },
    {
      field: key<SearchCandidate>('usefulScore'),
      headerName: 'Useful score',
      type: 'number',
      width: 160,
    },
    {
      field: key<SearchCandidate>('totalValidResults'),
      headerName: 'Total valid results',
      description: 'Number of companies in this search candidate sent to the user.',
      type: 'number',
      width: 160,
      hide: true,
    },
    {
      field: key<SearchCandidate>('opsNotes'),
      headerName: 'Notes',
      width: 400,
      renderCell: function editOpsNotes(params: GridCellParams) {
        const opsNotes = params.getValue(params.id, 'opsNotes') as string | undefined

        return (
          <DebouncedInput
            defaultValue={opsNotes}
            className={classes.opsNotesInput}
            onChange={(newValue) => {
              const thisSearchCandidate = searchCandidates?.find((searchCandidate) => searchCandidate.id === params.id)
              if (!thisSearchCandidate) {
                return
              }
              const isUseful = {
                [SelectableOption.Unknown]: undefined,
                [SelectableOption.Yes]: true,
                [SelectableOption.No]: false,
              }[thisSearchCandidate.isUseful]

              mutate({
                searchFunctionId: Number(scoutingCampaignId),
                searchCandidateId: Number(thisSearchCandidate.id),
                opsNotes: newValue,
                isUseful: isUseful,
              })
              thisSearchCandidate.opsNotes = newValue
            }}
            multiline
          />
        )
      },
    },
  ]

  return (
    <div className={classes.tableContainer}>
      <DataGrid
        rows={searchCandidates!}
        columns={columns}
        pageSize={20}
        disableSelectionOnClick
        disableVirtualization
      />
    </div>
  )
}

export function useCurrentSearchFunction(corporateId: number, searchFunctionId: number) {
  const { apiGetSearchFunctions } = getApiSearchFunctionHandlers()
  const { data = undefined } = useQuery(
    [SEARCH_FUNCTION_CACHE_STORE_ID, corporateId],
    ({ queryKey }: QueryFunctionContext<[string, EntityId]>) => {
      const [, corporateId] = queryKey
      return apiGetSearchFunctions(corporateId)
    },
    { suspense: true }
  )
  const searchFunction = data
    ? data.find((searchFunction) => searchFunction.id === Number(searchFunctionId))
    : undefined
  return searchFunction
}

const SEARCH_CANDIDATES_STORE_ID = 'search-candidates'

export function useSearchCandidatesStore(initialFilters: SearchCandidatesFilters) {
  const { state, actions: paginatedSearchActions } = usePaginatedSearch<SearchCandidate, SearchCandidatesFilters>(
    SEARCH_CANDIDATES_STORE_ID,
    searchCandidatesQuery,
    { initialFilters, urlSync: false },
    { suspense: true }
  )
  return { state, searchActions: { ...paginatedSearchActions } }
}

export async function searchCandidatesQuery(
  _key: string,
  filters: SearchCandidatesFilters,
  paginationOptions: PaginationOptions
) {
  const { apiGetSearchCandidates } = getApiSearchCandidatesHandlers()
  return apiGetSearchCandidates(filters, paginationOptions)
}

const useStyles = makeStyles()(({ spacing }: Theme) => ({
  challengeName: {
    marginBottom: spacing(1),
  },
  tableContainer: {
    height: '70vh',
  },
  longTextView: {
    whiteSpace: 'normal',
    maxHeight: '100%',
    overflow: 'scroll',
  },
  opsNotesInput: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
}))
